/**
 * Strategy Lab chart: one legend group (Vela indicator) per recipe chip —
 * "Setup · Engulfing", "Confluence 2 · Near order block", "Entry · Break close", "Volume".
 * Each group draws only its own chip: drawings tagged `group`, BE series tagged `chip_key`,
 * and the FE lines (MA / Bollinger / oscillators) the chip asked for.
 */
import type { TesseraLaneBOverlayId } from "../types/executionCandlesOverlays";
import {
  strategyRdConceptDisplayName,
  strategyRdEntryModeLabel,
} from "./strategyRdConceptCopy";
import {
  strategyRdLaneBFromConceptIds,
  strategyRdLaneBFromRecipeSlots,
} from "./strategyRdConceptIndicators";
import type { StrategyRdChartSeries } from "./strategyRdIndicatorSeries";

export type StrategyRdChartChipKind = "setup" | "confluence" | "entry" | "volume";

export type StrategyRdChartChip = {
  /** Drawing / series group: `setup`, `conf-N`, `entry`, `volume`. */
  key: string;
  kind: StrategyRdChartChipKind;
  /** Legend name, short: "Confluence 1 · TTM squeeze". */
  title: string;
  /** FE-computed lines this chip draws (MAs, Bollinger, oscillators). */
  lanes: TesseraLaneBOverlayId[];
};

type SlotLike = {
  id?: string | null;
  params?: Record<string, unknown> | null;
  /** Catalog display name stamped on the composer slot by BE. */
  name?: string | null;
};

/** Oscillator lanes draw in the chip's own panel; the rest go over price. */
export const STRATEGY_RD_PANEL_LANES: ReadonlySet<string> = new Set([
  "rsi",
  "adx",
  "atr",
  "stoch",
  "mfi",
  "macd",
]);

/** Chip key for BE confluence evidence / series: `conf-N`. */
export function strategyRdConfluenceChipKey(index: number): string {
  return `conf-${index}`;
}

function confluenceMatches(chipId: string, conceptId: string): boolean {
  const chip = chipId.trim().toLowerCase();
  const concept = conceptId.trim().toLowerCase();
  if (!chip || !concept) return false;
  if (chip === concept) return true;
  return new RegExp(`(^|_)${chip.replace(/[^a-z0-9_]/g, "")}`).test(concept);
}

/**
 * Tag each BE indicator series (keyed by BE slot key) with its composer chip (`chip_key`),
 * matched the same way as confluence evidence: exact id first, then family (`rsi` → `rsi_oversold`).
 */
export function strategyRdSeriesWithChipKeys(
  indicatorSeries: Record<string, unknown> | null | undefined,
  confluenceIds: readonly string[]
): Record<string, unknown> | null {
  if (!indicatorSeries) return null;
  const used = new Set<number>();
  const out: Record<string, unknown> = {};
  for (const [slotKey, raw] of Object.entries(indicatorSeries)) {
    const row = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null;
    if (!row) {
      out[slotKey] = raw;
      continue;
    }
    const concept = String(row.concept_id ?? slotKey.split("#")[0] ?? "").trim();
    const base = slotKey.split("#")[0]!.trim();
    const candidates = [
      confluenceIds.findIndex((id, i) => !used.has(i) && (id === base || id === concept)),
      confluenceIds.findIndex((id, i) => !used.has(i) && (confluenceMatches(id, base) || confluenceMatches(id, concept))),
      confluenceIds.findIndex((id) => confluenceMatches(id, base) || confluenceMatches(id, concept)),
    ];
    const index = candidates.find((i) => i >= 0);
    if (index != null) used.add(index);
    out[slotKey] = index != null ? { ...row, chip_key: strategyRdConfluenceChipKey(index) } : row;
  }
  return out;
}

/**
 * The chart's legend groups for a recipe, in composer order. `enabledLanes` = the FE lines the
 * chart already turns on (same-TF MAs, oscillators…); each goes to the first chip that asked
 * for it, leftovers to the setup.
 */
export function strategyRdChartChips(input: {
  setupId: string | null | undefined;
  setupSlot?: SlotLike | null;
  confluences: readonly SlotLike[];
  entryMode?: string | null;
  enabledLanes: readonly TesseraLaneBOverlayId[];
  /** A BE volume panel is present (volume confluences / up-down bars). */
  hasVolumePanel?: boolean;
  /** Catalog name for a concept id (the composer's picker names). */
  labelFor?: (id: string) => string | null | undefined;
}): StrategyRdChartChip[] {
  const nameOf = (slot: SlotLike | null | undefined, id: string): string =>
    (slot?.name || "").trim() || (input.labelFor?.(id) || "").trim() || strategyRdConceptDisplayName(id);
  const enabled = new Set<string>(input.enabledLanes);
  const claimed = new Set<string>();
  const claim = (lanes: readonly string[]): TesseraLaneBOverlayId[] => {
    const out: TesseraLaneBOverlayId[] = [];
    for (const lane of lanes) {
      if (!enabled.has(lane) || claimed.has(lane)) continue;
      claimed.add(lane);
      out.push(lane as TesseraLaneBOverlayId);
    }
    return out;
  };

  const setupId = (input.setupId || input.setupSlot?.id || "").trim();
  const setupLanes = setupId
    ? [
        ...strategyRdLaneBFromRecipeSlots({ setup: input.setupSlot ?? { id: setupId } }),
        ...strategyRdLaneBFromConceptIds([setupId]),
      ]
    : [];
  const chips: StrategyRdChartChip[] = [];
  const setupChip: StrategyRdChartChip | null = setupId
    ? {
        key: "setup",
        kind: "setup",
        title: `Setup · ${nameOf(input.setupSlot, setupId)}`,
        lanes: claim(setupLanes),
      }
    : null;
  if (setupChip) chips.push(setupChip);

  const seen = new Map<string, number>();
  input.confluences.forEach((slot, index) => {
    const id = (slot.id || "").trim();
    if (!id) return;
    const nth = (seen.get(id) ?? 0) + 1;
    seen.set(id, nth);
    chips.push({
      key: strategyRdConfluenceChipKey(index),
      kind: "confluence",
      title: `Confluence ${index + 1} · ${nameOf(slot, id)}${nth > 1 ? ` (${nth})` : ""}`,
      lanes: claim([
        ...strategyRdLaneBFromRecipeSlots({ confluences: [slot] }),
        ...strategyRdLaneBFromConceptIds([id]),
      ]),
    });
  });

  // Lines no chip asked for explicitly (e.g. enabled from bare concept ids) stay visible.
  const leftovers = Array.from(enabled).filter(
    (lane) => !claimed.has(lane) && !lane.startsWith("rd_")
  ) as TesseraLaneBOverlayId[];
  if (leftovers.length > 0 && setupChip) setupChip.lanes.push(...leftovers);

  if (input.hasVolumePanel) {
    chips.push({ key: "volume", kind: "volume", title: "Volume", lanes: [] });
  }
  const mode = (input.entryMode || "").trim();
  chips.push({
    key: "entry",
    kind: "entry",
    title: mode ? `Entry · ${strategyRdEntryModeLabel(mode)}` : "Entry",
    lanes: [],
  });
  return chips;
}

export const STRATEGY_RD_CHIP_TYPE_PREFIX = "tessera-chip-";

/** Lab core natives the chip groups replace (their drawings now live in the chips). */
export const STRATEGY_RD_CHIP_REPLACED_TYPES: readonly string[] = [
  "tessera-concepts",
  "tessera-pattern-bar",
  "tessera-trades",
  "tessera-rule-levels",
];

type ChipPanelSource =
  | { kind: "be"; seriesKey: string }
  | { kind: "osc"; lanes: string[] }
  | { kind: "macd" };

export type StrategyRdChipNativeSpec = {
  key: string;
  kind: StrategyRdChartChipKind;
  title: string;
  lanes: string[];
  /** Over-price indicator (null for the Volume group, which is a panel only). */
  mainType: string | null;
  panels: Array<{ type: string; title: string; source: ChipPanelSource; ownsMarkers: boolean }>;
};

const OSC_PANEL_LANES = ["rsi", "adx", "atr", "stoch", "mfi"];
export const STRATEGY_RD_CHIP_OSC_VALUE_FIELD: Record<string, "rsi" | "adx" | "atr_pct" | "stoch_k" | "mfi"> = {
  rsi: "rsi",
  adx: "adx",
  atr: "atr_pct",
  stoch: "stoch_k",
  mfi: "mfi",
};

function shortHash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Which Vela indicators each chip needs: its own (over price) + one per panel. */
export function strategyRdChipNativeSpecs(
  chips: readonly StrategyRdChartChip[],
  rdSeries: readonly StrategyRdChartSeries[]
): StrategyRdChipNativeSpec[] {
  return chips.map((chip) => {
    // The title is in the type id: a renamed chip gets a fresh legend entry.
    const base = `${STRATEGY_RD_CHIP_TYPE_PREFIX}${chip.key}-${shortHash(chip.title)}`;
    const subs = rdSeries.filter((e) =>
      chip.kind === "volume"
        ? e.pane === "volume"
        : e.pane === "sub" && e.group === chip.key
    );
    const panels: StrategyRdChipNativeSpec["panels"] = subs.map((e, i) => {
      const detail = e.title.includes(" · ") ? e.title.split(" · ").slice(-1)[0]! : e.lines[0]?.name ?? "";
      return {
        type: `${base}-p${i}`,
        title: i === 0 || !detail ? chip.title : `${chip.title} · ${detail}`,
        source: { kind: "be", seriesKey: e.key },
        ownsMarkers: i === 0,
      };
    });
    if (panels.length === 0) {
      const osc = chip.lanes.filter((l) => OSC_PANEL_LANES.includes(l));
      if (osc.length > 0) {
        panels.push({ type: `${base}-osc`, title: chip.title, source: { kind: "osc", lanes: osc }, ownsMarkers: true });
      }
      if (chip.lanes.includes("macd")) {
        panels.push({
          type: `${base}-macd`,
          title: osc.length > 0 ? `${chip.title} · MACD` : chip.title,
          source: { kind: "macd" },
          ownsMarkers: osc.length === 0,
        });
      }
    }
    return {
      key: chip.key,
      kind: chip.kind,
      title: chip.title,
      lanes: [...chip.lanes],
      mainType: chip.kind === "volume" ? null : base,
      panels,
    };
  });
}

